// Path-based routing for the dashboard SPA. Cloudflare Pages serves
// index.html for every path, so client-side code maps the path onto a
// section. Hash fragments are not used here; the AuthKit and Stripe
// callbacks both arrive on real paths or query params.

export type DashboardSection =
  | "dashboard"
  | "usage"
  | "keys"
  | "registrations"
  | "templates"
  | "receipts"
  | "witness-log"
  | "billing"
  | "settings"
  | "upgrade";

export const SECTION_PATHS: Record<DashboardSection, string> = {
  dashboard: "/dashboard",
  usage: "/usage",
  keys: "/keys",
  registrations: "/registrations",
  templates: "/templates",
  receipts: "/receipts",
  "witness-log": "/witness-log",
  billing: "/billing",
  settings: "/settings",
  upgrade: "/upgrade"
};

const PATH_TO_SECTION: Record<string, DashboardSection> = {
  "/dashboard": "dashboard",
  "/usage": "usage",
  "/keys": "keys",
  "/registrations": "registrations",
  "/templates": "templates",
  "/receipts": "receipts",
  "/witness-log": "witness-log",
  "/billing": "billing",
  "/settings": "settings",
  "/upgrade": "upgrade"
};

// Map a URL pathname to a dashboard section. The root path ("/") and
// any unrecognized path fall through to "dashboard" so the landing
// page is the default for both fresh signups and stale bookmarks.
// The receipts and witness-log detail paths nest a sub-route; they
// still resolve to their parent section so the sidebar highlights
// correctly while App.tsx decides between list and detail.
export function pathToSection(pathname: string): DashboardSection {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/") return "dashboard";
  if (RECEIPT_DETAIL_PATH.test(normalized)) return "receipts";
  if (WITNESS_LOG_DETAIL_PATH.test(normalized)) return "witness-log";
  return PATH_TO_SECTION[normalized] ?? "dashboard";
}

export function sectionToPath(section: DashboardSection): string {
  return SECTION_PATHS[section];
}

// Receipt ids in the system follow the same character set as chain ids
// (see apps/broker/src/handlers/receipt.ts). The pattern is kept
// permissive so legacy intermediary-era ids and broker-generated ids
// both route.
const RECEIPT_ID_SEGMENT = "[A-Za-z0-9._:-]{1,128}";
const RECEIPT_DETAIL_PATH = new RegExp(`^/receipts/(${RECEIPT_ID_SEGMENT})$`);

// Witness log entries are addressed by position, a positive integer.
const WITNESS_LOG_DETAIL_PATH = /^\/witness-log\/(\d+)$/;

// Returns the receipt id when the pathname is /receipts/<id>, or null
// otherwise. App.tsx uses this to decide whether to render the list or
// the detail page under the "receipts" section.
export function parseReceiptDetailId(pathname: string): string | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const match = RECEIPT_DETAIL_PATH.exec(normalized);
  return match ? match[1] : null;
}

export function receiptDetailPath(receiptId: string): string {
  return `/receipts/${receiptId}`;
}

// Returns the entry position when the pathname is /witness-log/<n>,
// or null otherwise. App.tsx uses this to decide whether to render
// the list or the detail page under the "witness-log" section.
export function parseWitnessLogDetailPosition(pathname: string): number | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const match = WITNESS_LOG_DETAIL_PATH.exec(normalized);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function witnessLogDetailPath(position: number): string {
  return `/witness-log/${position}`;
}

// Returns true when the pathname is an auth-flow path that must not
// be handled as a dashboard section (e.g. the AuthKit callback). The
// caller is responsible for the actual handoff; this just gates the
// section router so /auth/callback doesn't render the dashboard
// landing while the exchange is in flight.
export function isAuthPath(pathname: string): boolean {
  return pathname === "/auth/callback" || pathname === "/login";
}
